import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { EXTRA_PATHS, ROLE_PATHS } from "../config/config";
import { useAuth } from "../data/providers/AuthProvider";
import { useUserData } from "../data/providers/UserDataProvider";

const PrivateRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const data = useUserData();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      data?.reloadUserData();
      return;
    }
    data?.clearUserData();
  }, [user]);

  if (loading || data?.loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (data?.hasError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          No pudimos validar tu sesion en este momento.
        </Typography>
        <Button variant="contained" onClick={() => void data.reloadUserData()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!data?.data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const role = data.data.role ?? "USER";

  if (role === "USER" && location.pathname === "/dashboard") {
    return <Navigate to="/dashboard/plan" replace />;
  } else if (role === "ADMIN" && location.pathname === "/dashboard") {
    return <Navigate to="/dashboard/admin/users" replace />;
  }

  const allowedRoutes = [
    ...(ROLE_PATHS[role as keyof typeof ROLE_PATHS] || []),
    ...(EXTRA_PATHS[role as keyof typeof EXTRA_PATHS] || []),
  ];

  const currentPath = location.pathname;
  const hasAccess = allowedRoutes.some((path) => currentPath.startsWith(path));

  if (!hasAccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
          bgcolor: "background.default",
          p: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          🚫 Acceso denegado
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          No tienes permiso para acceder a esta sección.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => {
            const redirectTo =
              role === "ADMIN" ? "/dashboard" : "/dashboard/plan";
            window.location.href = redirectTo;
          }}
        >
          Ir a mi panel
        </Button>
      </Box>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
